<script>
  import { navigate } from "svelte-routing";
  import { config } from "../../stores/config.js";
  import api from "../../services/api.js";
  import Logo from "../../components/Logo.svelte";

  let isLoading = false;
  let error = null;
  let success = null;

  // Waitlist form state
  let waitlistName = '';
  let waitlistEmail = '';
  let waitlistPhone = '';
  let isSubmittingWaitlist = false;

  // Redirect to home if auth is disabled
  $: if (!$config.loading && !$config.authEnabled) {
    // Auth is disabled, stay on this page but show waitlist
  }

  async function handleGoogleLogin() {
    isLoading = true;
    error = null;

    try {
      const response = await fetch(api.getGoogleLoginUrl());
      const data = await response.json();

      if (data.authorization_url) {
        // Redirect to Google's OAuth page
        window.location.href = data.authorization_url;
      } else {
        error = "Failed to get authorization URL";
      }
    } catch (err) {
      error = "Failed to initiate Google login. Please try again.";
      console.error("Google login error:", err);
    } finally {
      isLoading = false;
    }
  }

  async function handleWaitlistSubmit(e) {
    e.preventDefault();
    isSubmittingWaitlist = true;
    error = null;
    success = null;

    try {
      const data = {
        name: waitlistName.trim(),
        email: waitlistEmail.trim(),
        phone_number: waitlistPhone.trim() || null
      };

      const result = await api.joinWaitlist(data);
      success = result.message;

      // Clear the form
      waitlistName = '';
      waitlistEmail = '';
      waitlistPhone = '';
    } catch (err) {
      error = err.message || "Failed to join waitlist. Please try again.";
      console.error("Waitlist error:", err);
    } finally {
      isSubmittingWaitlist = false;
    }
  }
</script>

<div class="w-full max-w-md">
  <div class="card card-pad">
    {#if !$config.authEnabled}
      <!-- Waitlist signup -->
      <div class="mb-6">
        <Logo size={32} />
      </div>
      <h1 class="text-xl font-semibold text-ink">Join the waitlist</h1>
      <p class="text-sm text-ink-secondary mt-1 mb-6">
        We're currently awaiting approval from Alpaca Markets to enable account creation. Join our waitlist to be notified when signups open!
      </p>

      {#if success}
        <div class="banner banner-success">
          {success}
        </div>
      {:else}
        <form on:submit={handleWaitlistSubmit} class="space-y-4">
          <div>
            <label class="label" for="name">
              Name <span class="text-red-600">*</span>
            </label>
            <input
              type="text"
              id="name"
              bind:value={waitlistName}
              required
              class="input"
              placeholder="Your full name"
            />
          </div>

          <div>
            <label class="label" for="email">
              Email <span class="text-red-600">*</span>
            </label>
            <input
              type="email"
              id="email"
              bind:value={waitlistEmail}
              required
              class="input"
              placeholder="your@email.com"
            />
          </div>

          <div>
            <label class="label" for="phone">
              Phone number <span class="font-normal text-ink-tertiary">(optional — USA only)</span>
            </label>
            <input
              type="tel"
              id="phone"
              bind:value={waitlistPhone}
              class="input"
              placeholder="(555) 123-4567"
            />
            <p class="text-xs text-ink-tertiary mt-1.5">We may send occasional text updates. Standard rates apply.</p>
          </div>

          {#if error}
            <div class="banner banner-danger">
              {error}
            </div>
          {/if}

          <button
            type="submit"
            disabled={isSubmittingWaitlist}
            class="btn btn-primary w-full"
          >
            {#if isSubmittingWaitlist}
              <i class="fas fa-spinner fa-spin"></i>
              Joining waitlist…
            {:else}
              Join waitlist
            {/if}
          </button>
        </form>
      {/if}
    {:else}
      <!-- Sign in -->
      <div class="mb-6">
        <Logo size={32} />
      </div>
      <h1 class="text-xl font-semibold text-ink">Welcome back</h1>
      <p class="text-sm text-ink-secondary mt-1 mb-6">
        Sign in to continue to Moira Finance.
      </p>

      <button
        on:click={handleGoogleLogin}
        disabled={isLoading}
        class="btn btn-outline w-full"
      >
        {#if isLoading}
          <i class="fas fa-spinner fa-spin"></i>
          Connecting…
        {:else}
          <img alt="Google" class="w-5 h-5" src="/assets/img/google.svg" />
          Continue with Google
        {/if}
      </button>

      {#if error}
        <div class="banner banner-danger mt-4">
          {error}
        </div>
      {/if}

      <div class="flex items-center gap-3 my-6">
        <span class="h-px bg-line flex-1"></span>
        <span class="text-xs text-ink-tertiary">or</span>
        <span class="h-px bg-line flex-1"></span>
      </div>

      <p class="text-sm text-ink-secondary text-center">
        Click the Google button above to sign in or create an account.
        We use Google Sign-In for secure authentication.
      </p>
    {/if}
  </div>

  <p class="text-xs text-ink-tertiary text-center mt-6">
    By continuing you agree to our
    <a href="/terms" class="underline hover:text-ink-secondary">Terms of Service</a>
    and
    <a href="/privacy" class="underline hover:text-ink-secondary">Privacy Policy</a>.
  </p>
  <p class="text-xs text-ink-tertiary text-center mt-2">
    <a href="/" class="underline hover:text-ink-secondary">Back to home</a>
  </p>
</div>
